import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { http } from "@/lib/api";
import { toast } from "sonner";
import { User, Phone, Mail, ShieldCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";

/**
 * Reusable "My Profile" card — lets the currently-signed-in user update
 * their name, phone and email. Wire the same POST /api/accounts/update
 * endpoint used by parents. Works for teachers / school admins / parents
 * (super admins can use the User Configuration panel instead).
 */
export function ProfileCard({ onSaved, title = "My profile", subtitle }) {
  const { user, refresh } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [phone, setPhone] = useState(user?.phone || "");
  const [email, setEmail] = useState(user?.email || "");
  const [saving, setSaving] = useState(false);

  const dirty = name !== (user?.name || "") || phone !== (user?.phone || "") || email !== (user?.email || "");

  const save = async () => {
    setSaving(true);
    try {
      const patch = {};
      if (name !== user?.name) patch.name = name;
      if (phone !== user?.phone) patch.phone = phone;
      if (email !== user?.email) patch.email = email;
      const { data } = await http.post("/accounts/update", patch);
      await refresh?.();
      toast.success("Profile updated");
      onSaved?.(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card data-testid="profile-card" className="max-w-xl">
      <CardHeader>
        <CardTitle className="font-heading flex items-center gap-2">
          <User className="w-4 h-4" /> {title}
        </CardTitle>
        {subtitle && <div className="text-xs text-slate-500 mt-1">{subtitle}</div>}
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <User className="w-3 h-3" /> Name
          </Label>
          <Input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)} className="mt-2" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Phone className="w-3 h-3" /> Phone
          </Label>
          <Input data-testid="profile-phone-input" value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-2" placeholder="e.g. 9876543210" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-widest text-slate-500 flex items-center gap-1">
            <Mail className="w-3 h-3" /> Email
          </Label>
          <Input data-testid="profile-email-input" value={email} onChange={(e) => setEmail(e.target.value)} className="mt-2" />
          <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-1">
            <ShieldCheck className="w-3 h-3 text-emerald-500" /> OTPs will be sent to this email on next login.
          </div>
        </div>
        <Button
          data-testid="profile-save-btn"
          onClick={save}
          disabled={!dirty || saving}
          className="w-full bg-sky-500 hover:bg-sky-600"
        >
          {saving ? "Saving…" : "Save changes"}
        </Button>
      </CardContent>
    </Card>
  );
}
